import { CLI_VERIFICATION_METHOD, CLI_VERIFICATION_STATUS, CliSignalledNumber } from '../../../core/services/phone-service-api.model';
import { PhoneNumber } from '../../cc-numbers/cc-numbers.model';

/** Contract extension that carries the SIP account (bookingmgmt `ExtensionName.SIP_TRUNK`). */
export const SIP_TRUNK_EXTENSION = 'SIPTrunk';
/** Carrier of the numbers booked together with the SIP account. */
export const SIP_CARRIER = 'OUTBOX';
/** Number type the SIP number is booked as. */
const CALLCENTER_TYPE = 'CALLCENTER';
/** Own-number signalling is only regulated (and the SIP account only sold) for Germany. */
const REGULATED_COUNTRY = 'DE';

/**
 * - `notRequired` — nothing to do here (outside Germany, or exempt)
 * - `done` — booked / present
 * - `open` — missing and fixable
 * - `unavailable` — missing, and the contract plan does not offer it
 * - `unknown` — the source could not be read
 */
export type PrerequisiteState = 'notRequired' | 'done' | 'open' | 'unavailable' | 'unknown';

/** State of the outbound number itself: set at all, and proven. */
export type OutboundNumberState = 'notRequired' | 'missing' | 'unverified' | 'verified' | 'unknown';

/** What the contract says about the SIP-account extension; null when the contract could not be read. */
export interface SipExtensionInfo {
  booked: boolean;
  /** Monthly fee in the plan; null when the plan does not offer the extension. */
  monthlyFee: number | null;
}

export interface OutboundReadinessInput {
  /** ISO country of the phone service (from its country prefix). */
  country: string | null;
  outboundNumber: string | null;
  /** Verification state; null when it could not be read. */
  cli: CliSignalledNumber | null;
  extension: SipExtensionInfo | null;
  /** This phone service's numbers; null when they could not be read. */
  numbers: readonly PhoneNumber[] | null;
}

export interface OutboundReadiness {
  /** False outside Germany: every row reads "not required" and there is nothing to send. */
  applies: boolean;
  extension: PrerequisiteState;
  /** Monthly fee of the extension when it is open, for display. */
  extensionFee: number | null;
  sipNumber: PrerequisiteState;
  /** The booked SIP number, when there is one. */
  sipNumberValue: string | null;
  number: OutboundNumberState;
  /** VOICEBOT | GRANDFATHERED when verified. */
  verificationMethod: string | null;
  /** Whether a SIP order link would fix something (extension or SIP number open or unknown). */
  sipAccountNeeded: boolean;
  /** Why sending cannot help even though the SIP account is missing; null when it can. */
  sipAccountBlockedReason: string | null;
}

/**
 * Derives the outbound prerequisites of one phone service from data AMIE already loads: contract
 * extensions, the phone service's numbers and `cli-verification/number/signalled`.
 *
 * Mirrors the connect page's reading (`deriveSetupRequirement`, PS-6495/PS-6589): the SIP account
 * is the `SIPTrunk` extension plus a CALLCENTER number on the OUTBOX carrier, booked together; a
 * GRANDFATHERED verification is exempt from the SIP-account requirement (phoneservice
 * `CliSignalledNumberResponse`). Unknown sources never claim "done": they read `unknown`, and the
 * send action stays available because bookingmgmt answers an existing SIP account with COMPLETED.
 *
 * @ticket PS-6598
 */
export function deriveOutboundReadiness(input: OutboundReadinessInput): OutboundReadiness {
  if (input.country !== REGULATED_COUNTRY) {
    return {
      applies: false,
      extension: 'notRequired',
      extensionFee: null,
      sipNumber: 'notRequired',
      sipNumberValue: null,
      number: 'notRequired',
      verificationMethod: null,
      sipAccountNeeded: false,
      sipAccountBlockedReason: null,
    };
  }

  const number = numberState(input);
  const verificationMethod = number === 'verified' ? input.cli?.verificationMethod ?? null : null;
  const exempt = verificationMethod === CLI_VERIFICATION_METHOD.GRANDFATHERED;

  const sipNumber = input.numbers?.find(isSipNumber);
  let extension = extensionState(input);
  let sipNumberState: PrerequisiteState = input.numbers === null ? 'unknown' : sipNumber ? 'done' : 'open';
  if (exempt) {
    if (extension !== 'done') extension = 'notRequired';
    if (sipNumberState !== 'done') sipNumberState = 'notRequired';
  }

  const missing = (s: PrerequisiteState) => s === 'open' || s === 'unavailable' || s === 'unknown';
  const sipAccountNeeded = missing(extension) || missing(sipNumberState);

  return {
    applies: true,
    extension,
    extensionFee: extension === 'open' ? input.extension?.monthlyFee ?? null : null,
    sipNumber: sipNumberState,
    sipNumberValue: sipNumber?.number ?? null,
    number,
    verificationMethod,
    sipAccountNeeded,
    sipAccountBlockedReason: extension === 'unavailable' ? 'the contract plan does not offer the SIP account' : null,
  };
}

/** Label + `hlm-badge` variant of a row (Style Guide §7.2: success = done, accent = needs action). */
export interface ReadinessBadge {
  label: string;
  variant: 'success' | 'accent' | 'secondary' | 'outline';
}

export function prerequisiteBadge(state: PrerequisiteState): ReadinessBadge {
  switch (state) {
    case 'done': return { label: 'Booked', variant: 'success' };
    case 'open': return { label: 'Open', variant: 'accent' };
    case 'unavailable': return { label: 'Not in plan', variant: 'secondary' };
    case 'unknown': return { label: 'Unknown', variant: 'outline' };
    default: return { label: 'Not required', variant: 'outline' };
  }
}

export function numberStateBadge(readiness: OutboundReadiness): ReadinessBadge {
  switch (readiness.number) {
    case 'verified':
      return readiness.verificationMethod === CLI_VERIFICATION_METHOD.GRANDFATHERED
        ? { label: 'Verified (grandfathered)', variant: 'success' }
        : { label: 'Verified', variant: 'success' };
    case 'unverified': return { label: 'Not verified', variant: 'accent' };
    case 'missing': return { label: 'No number', variant: 'accent' };
    case 'unknown': return { label: 'Unknown', variant: 'outline' };
    default: return { label: 'Not required', variant: 'outline' };
  }
}

function numberState(input: OutboundReadinessInput): OutboundNumberState {
  if (!input.outboundNumber?.trim()) return 'missing';
  if (!input.cli) return 'unknown';
  return input.cli.verificationStatus === CLI_VERIFICATION_STATUS.VERIFIED ? 'verified' : 'unverified';
}

/** `sipTrunkBooked` and the booked extensions are two reads of the same fact; either one is enough. */
function extensionState(input: OutboundReadinessInput): PrerequisiteState {
  if (input.extension?.booked || input.cli?.sipTrunkBooked) return 'done';
  if (!input.extension) return 'unknown';
  return input.extension.monthlyFee == null ? 'unavailable' : 'open';
}

function isSipNumber(n: PhoneNumber): boolean {
  return n.type === CALLCENTER_TYPE && n.carrier === SIP_CARRIER;
}
